import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Header from "@/components/Header";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Package, Plus, Trash2, AlertTriangle, Edit2, Save, X } from "lucide-react";

const CATEGORIES = [
  { key: "ingredient", label: "Ingredient" },
  { key: "packaging",  label: "Packaging" },
  { key: "equipment",  label: "Equipment" },
  { key: "other",      label: "Other" },
];

const emptyItem = { name: "", category: "ingredient", unit: "g", current_qty: 0, low_alert: 100, cost_per_unit: 0, notes: "" };

export default function Inventory() {
  const { user, loading } = useAuth();
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null); // item id or 'new'
  const [draft, setDraft] = useState(emptyItem);
  const [filter, setFilter] = useState("all");
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/inventory").then((r) => setItems(r.data));

  useEffect(() => {
    if (loading) return;
    if (!user || user === false) { nav("/login"); return; }
    load();
  }, [user, loading, nav]);

  const startNew = () => { setDraft(emptyItem); setEditing("new"); };
  const startEdit = (it) => { setDraft({ ...it }); setEditing(it.id); };
  const cancel = () => { setEditing(null); setDraft(emptyItem); };

  const save = async () => {
    if (!draft.name.trim()) { toast.error("Name required"); return; }
    setBusy(true);
    try {
      const payload = { ...draft, current_qty: parseFloat(draft.current_qty) || 0, low_alert: parseFloat(draft.low_alert) || 0, cost_per_unit: parseFloat(draft.cost_per_unit) || 0 };
      if (editing === "new") await api.post("/inventory", payload);
      else await api.patch(`/inventory/${editing}`, payload);
      toast.success("Saved");
      cancel();
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    setBusy(false);
  };

  const del = async (id) => {
    if (!window.confirm("Delete this item?")) return;
    await api.delete(`/inventory/${id}`);
    load();
  };

  if (loading || !user) return null;
  const visible = filter === "all" ? items : filter === "low" ? items.filter((i) => i.is_low) : items.filter((i) => i.category === filter);
  const lowCount = items.filter((i) => i.is_low).length;

  return (
    <>
      <Header />
      <main className="max-w-5xl mx-auto px-4 sm:px-6 py-6 lg:py-10">
        <div className="mb-6 flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="inline-flex items-center gap-1.5 text-xs uppercase tracking-widest text-[#857163]"><Package className="w-3.5 h-3.5" style={{ color: "#8EAC50" }} /><span>Business tool</span></div>
            <h1 className="font-heading text-4xl sm:text-5xl font-semibold text-[#3B2C24] mt-1">Inventory & <span style={{ color: "#8EAC50" }}>Stock</span> Manager</h1>
            <p className="mt-2 text-[#857163] max-w-2xl">Track flour, sugar, packaging — get flagged when running low.</p>
          </div>
          <button onClick={startNew} className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-full text-white font-semibold text-sm hover:opacity-95 transition shrink-0" style={{ background: "#8EAC50" }} data-testid="add-item-btn"><Plus className="w-4 h-4" /> Add item</button>
        </div>

        <div className="flex flex-wrap gap-2 mb-4">
          {[{key:"all",label:"All"},{key:"low",label:`Low stock (${lowCount})`},...CATEGORIES].map((f) => (
            <button key={f.key} onClick={() => setFilter(f.key)} className={`px-3 py-1.5 rounded-full text-xs font-medium border transition ${filter === f.key ? "bg-[#3B2C24] text-white border-[#3B2C24]" : "bg-white border-[#EBDCD1] text-[#3B2C24] hover:bg-[#F9EFE6]"}`} data-testid={`filter-${f.key}`}>{f.label}</button>
          ))}
        </div>

        {editing !== null && (
          <div className="cp-card p-5 mb-4" data-testid="edit-form">
            <div className="grid sm:grid-cols-[1fr_150px_100px] gap-3">
              <label><span className="text-xs text-[#857163]">Name</span><input className="cp-input mt-1" value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} placeholder="Bread flour" data-testid="draft-name" /></label>
              <label><span className="text-xs text-[#857163]">Category</span><select className="cp-input mt-1" value={draft.category} onChange={(e) => setDraft({ ...draft, category: e.target.value })} data-testid="draft-category">{CATEGORIES.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}</select></label>
              <label><span className="text-xs text-[#857163]">Unit</span><input className="cp-input mt-1" value={draft.unit} onChange={(e) => setDraft({ ...draft, unit: e.target.value })} placeholder="g / kg / oz / boxes" data-testid="draft-unit" /></label>
            </div>
            <div className="grid sm:grid-cols-3 gap-3 mt-3">
              <label><span className="text-xs text-[#857163]">Current qty</span><input type="number" step={0.1} className="cp-input mt-1" value={draft.current_qty} onChange={(e) => setDraft({ ...draft, current_qty: e.target.value })} data-testid="draft-qty" /></label>
              <label><span className="text-xs text-[#857163]">Alert when below</span><input type="number" step={0.1} className="cp-input mt-1" value={draft.low_alert} onChange={(e) => setDraft({ ...draft, low_alert: e.target.value })} data-testid="draft-alert" /></label>
              <label><span className="text-xs text-[#857163]">Cost per unit (opt.)</span><input type="number" step={0.01} className="cp-input mt-1" value={draft.cost_per_unit} onChange={(e) => setDraft({ ...draft, cost_per_unit: e.target.value })} data-testid="draft-cost" /></label>
            </div>
            <input className="cp-input mt-3" placeholder="Notes (optional)" value={draft.notes} onChange={(e) => setDraft({ ...draft, notes: e.target.value })} data-testid="draft-notes" />
            <div className="mt-3 flex gap-2 justify-end">
              <button onClick={cancel} className="cp-btn-ghost inline-flex items-center gap-1.5"><X className="w-3.5 h-3.5" /> Cancel</button>
              <button onClick={save} disabled={busy} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-white font-semibold text-sm hover:opacity-90 disabled:opacity-50" style={{ background: "#8EAC50" }} data-testid="save-item"><Save className="w-3.5 h-3.5" /> Save</button>
            </div>
          </div>
        )}

        {visible.length === 0 ? (
          <div className="cp-card p-8 text-center text-[#857163]">No items to show.</div>
        ) : (
          <div className="cp-card overflow-hidden">
            <div className="grid grid-cols-[1fr_100px_100px_100px_80px] gap-3 px-4 py-3 bg-[#F9EFE6] text-[11px] uppercase tracking-wider text-[#857163] font-semibold">
              <div>Item</div><div>Category</div><div className="text-right">Qty</div><div className="text-right">Alert</div><div></div>
            </div>
            {visible.map((i) => (
              <div key={i.id} className={`grid grid-cols-[1fr_100px_100px_100px_80px] gap-3 px-4 py-3 border-t border-[#EBDCD1] items-center ${i.is_low ? "bg-[#C2185B]/5" : ""}`} data-testid={`item-${i.id}`}>
                <div className="min-w-0">
                  <div className="text-sm font-medium text-[#3B2C24] flex items-center gap-1.5">
                    {i.is_low && <AlertTriangle className="w-3.5 h-3.5" style={{ color: "#C2185B" }} />}
                    {i.name}
                  </div>
                  {i.notes && <div className="text-xs text-[#857163] truncate">{i.notes}</div>}
                </div>
                <div className="text-xs text-[#857163] capitalize">{i.category}</div>
                <div className="text-right font-mono tabular-nums text-sm">{i.current_qty}<span className="text-xs text-[#857163] ml-0.5">{i.unit}</span></div>
                <div className="text-right font-mono tabular-nums text-xs text-[#857163]">{i.low_alert}{i.unit}</div>
                <div className="flex justify-end gap-1">
                  <button onClick={() => startEdit(i)} className="p-1.5 rounded-full hover:bg-[#F9EFE6]" data-testid={`edit-${i.id}`}><Edit2 className="w-3 h-3" /></button>
                  <button onClick={() => del(i.id)} className="p-1.5 rounded-full hover:bg-red-50 text-red-500" data-testid={`del-${i.id}`}><Trash2 className="w-3 h-3" /></button>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>
    </>
  );
}
